import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid2 as Grid, Link, TextField, Typography } from "@mui/material";
import MuiPhone from "../../components/MuiPhone";
import PasswordStrengthIndicator from "../../components/common/PasswordStrengthIndicator";
import SignupSplitLayout from "./SignupSplitLayout";
import { useNavigate } from "react-router-dom";

interface Props {
  formData: any;
  formErrors: any;
  handleInputChange: (e: any) => void;
  handlePhoneChange: (val: any) => void;
  isAutoRegistering: boolean;
  isLoading: boolean;
  onContinue: () => void;
}

const SignupStepDetails: React.FC<Props> = ({ formData, formErrors, handleInputChange, handlePhoneChange, isAutoRegistering, isLoading, onContinue }) => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");

  useEffect(() => {
    setFullName([formData.firstName, formData.lastName].filter(Boolean).join(" "));
  }, [formData.firstName, formData.lastName]);

  const handleFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let nextValue = e.target.value.replace(/\s+/g, " ").trimStart();
    const nameParts = nextValue.split(" ");

    if (nameParts.length > 2) {
      nextValue = `${nameParts[0]} ${nameParts.slice(1).join("")}`;
    }

    setFullName(nextValue);

    const [firstName = "", lastName = ""] = nextValue.split(" ");
    handleInputChange({ target: { name: "firstName", value: firstName } });
    handleInputChange({ target: { name: "lastName", value: lastName } });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading && !isAutoRegistering) {
      onContinue();
    }
  };

  return (
    <SignupSplitLayout>
      <Box sx={{ width: "100%", maxWidth: "580px", p:{xs:4, sm:4} }}>
      <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "grey.900",
            mb: 0.5,
            textAlign: "center",
            fontSize: { xs: "18px", sm: "22px" },
          }}
        >
          Turn webinars into revenue.
        </Typography>
        <Typography
          sx={{
            color: "grey.600",
            mb: 2,
            textAlign: "center",
            fontSize: "14px",
          }}
        >
          Launch in minutes with EasyWebinar AI.
        </Typography>
        <Grid container rowSpacing={2} columnSpacing={2} mb={2}>
          <Grid size={{ xs: 12 }}>
            <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
              Name
            </Typography>
            <TextField
              variant="outlined"
              placeholder="John Doe"
              name="fullName"
              value={fullName}
              onChange={handleFullNameChange}
              error={!!(formErrors.firstName || formErrors.lastName)}
              helperText={formErrors.firstName || formErrors.lastName || " "}
              FormHelperTextProps={{ sx: { lineHeight: 1 } }}
              required
              inputProps={{ maxLength: 50 }}
              sx={{
                position: "relative",
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "14px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                "& .MuiOutlinedInput-input": { height: "1em", padding: "14px" },
                "& .MuiFormHelperText-root": {
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  mt: "4px",
                  mr: 0,
                  textAlign: "right",
                },
              }}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
              Work email
            </Typography>
            <TextField
              variant="outlined"
              placeholder="john@company.com"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!formErrors.email}
              FormHelperTextProps={{ sx: { lineHeight: 1 } }}
              helperText={formErrors.email}
              required
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "14px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                "& .MuiOutlinedInput-input": { height: "1em", padding: "14px" },
                "& .MuiFormHelperText-root": {
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  mt: "4px",
                  mr: 0,
                  textAlign: "right",
                },
              }}
              fullWidth
            />
          </Grid>

          {/* <Grid size={{ xs: 12, sm: 6 }}>
            <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
              Phone number
            </Typography>
            <MuiPhone
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "14px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                "& .MuiOutlinedInput-input": { height: "1em", padding: "14px", paddingLeft: "0px", },
                "& .MuiSelect-select": { padding: "10px", outline: "none", fontSize: "14px" },
                "& .MuiSelect-select:focus": { outline: "none" },
              }}
            />
          </Grid> */}

          <Grid size={{ xs: 12 }}>
            <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
              Create password
            </Typography>
            <TextField
              variant="outlined"
              type="password"
              placeholder="Create a strong password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              error={!!formErrors.password}
              helperText={formErrors.password || " "}
              FormHelperTextProps={{ sx: { lineHeight: 1 } }}
              disabled={isAutoRegistering}
              required
              autoComplete="new-password"
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "14px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                "& .MuiOutlinedInput-input": { height: "1em", padding: "14px" },
                "& .MuiFormHelperText-root": {
                  position: "absolute",
                  top: {xs: "calc(100% + 0px)", sm: "calc(100% + 20px)"},
                  right: 0,
                  mt: "4px",
                  mr: 0,
                  textAlign: "right",
                },
              }}
              fullWidth
            />
            {/* <Box sx={{ height: 10 }}/> */}
            <PasswordStrengthIndicator password={formData.password} transparent compact />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          onClick={onContinue}
          disabled={isLoading || isAutoRegistering}
          disableElevation
          fullWidth
          sx={{
            mt: 3,
            py: { xs: 1.2, sm: 1.6 },
            lineHeight: "1.3",
            borderRadius: "8px",
            color: "#fff !important",
            fontWeight: 600,
            fontSize: { xs: "14px", sm: "16px" },
            textTransform: "none",
            "&.Mui-disabled": {
              bgcolor: "#cccccc !important",
              color: "#666666 !important",
            },
          }}
        >
          {isLoading || isAutoRegistering ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Continue"}
        </Button>
        <Typography
          sx={{
            fontSize: "12px",
            color: "#9ca3af",
            textAlign: "center",
            lineHeight: 1.6,
            mt: 2,
          }}
        >
          By continuing, you agree to our{" "}
          <Link
            href="https://easywebinar.com/terms-conditions/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: "underline",
              color: "#667EEA",
              whiteSpace: "nowrap",
              "&:hover": { color: "#667EEA" },
            }}
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            href="https://easywebinar.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: "underline",
              color: "#667EEA",
              whiteSpace: "nowrap",
              "&:hover": { color: "#667EEA" },
            }}
          >
            Privacy Policy
          </Link>
        </Typography>

        <Typography sx={{ fontSize: "14px", textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 3 }}>
          <Typography component="span" sx={{ color: "#6B7280", fontSize: "14px" }}>
            Already have an account?{" "}
          </Typography>
          <Link
            component="button"
            onClick={() => navigate("/login")}
            sx={{
              fontWeight: 500,
              color: "#6066E9",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Log in
          </Link>
        </Typography>
        
      </Box>
    </SignupSplitLayout>
  );
};

export default SignupStepDetails;

